import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiClient } from '@core/network/apiClient';
import { fetchAllPages } from '@core/network/paginate';
import type { CountBadge } from '@core/types/models';
import { dashboardKeys } from '@features/dashboard/services/dashboard.service';
import type { NotificationItem } from '@features/notifications/types/notification.types';

/** How often the badge re-checks. The count is a bounded index probe on the
 *  server, so this is cheap — but it is still a poll, and a minute is the
 *  shortest interval that reads as "live" without being a heartbeat. */
const UNREAD_POLL_MS = 60_000;

export const notificationKeys = {
  all: ['notifications'] as const,
  feed: () => [...notificationKeys.all, 'feed'] as const,
  unread: () => [...notificationKeys.all, 'unread'] as const,
};

/** The badge. Separate from the feed on purpose: this polls, the feed does not. */
export function useUnreadCount(): UseQueryResult<CountBadge> {
  return useQuery({
    queryKey: notificationKeys.unread(),
    queryFn: async () => {
      const { data } = await apiClient.get<CountBadge>('/notifications/unread-count');
      return data;
    },
    refetchInterval: UNREAD_POLL_MS,
  });
}

/** The feed, newest first. Fetched when the bell is opened, not on a timer. */
export function useNotifications(enabled: boolean): UseQueryResult<NotificationItem[]> {
  return useQuery({
    queryKey: notificationKeys.feed(),
    queryFn: () => fetchAllPages<NotificationItem>(apiClient, '/notifications'),
    enabled,
  });
}

/** Acknowledge notifications, by id or all at once. */
export function useMarkRead(): UseMutationResult<void, Error, { ids?: number[]; all?: boolean }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      await apiClient.post('/notifications/read', body.all ? { all: true } : { ids: body.ids });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      // The dashboard renders the same count, so it goes stale at the same moment.
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.summary() });
    },
  });
}
